import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { AlertOctagon, ArrowUpRight, Maximize2 } from "lucide-react";
import { z } from "zod";

import { useRisksList, useUpdateRisk } from "@/application/use-cases/risks/use-risks.queries";
import { useStakeholdersList } from "@/application/use-cases/stakeholders/use-stakeholders.queries";
import { RiskDrawerContent } from "@/presentation/components/risks/risk-drawer-content";
import { RouteHero } from "@/presentation/components/shell/route-hero";
import { StatusDot, STATUS_LABEL } from "@/presentation/components/status/status-dot";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@monorepo-apps/ui/src/components/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@monorepo-apps/ui/src/components/sheet";
import type { Rag, RiskRow, Status } from "@/infrastructure/supabase/database.types";

const risksSearchSchema = z.object({
  id: z.string().optional(),
});

export const Route = createFileRoute("/_app/risks/")({
  validateSearch: risksSearchSchema,
  component: RisksListPage,
});

function RisksListPage() {
  const risksQ = useRisksList();
  const stakeholdersQ = useStakeholdersList();
  const updateRisk = useUpdateRisk();
  const navigate = useNavigate({ from: Route.fullPath });
  const { id: openId } = Route.useSearch();
  const risks = risksQ.data ?? [];
  const ownersById = new Map((stakeholdersQ.data ?? []).map((s) => [s.id, s.name]));

  const counts = {
    red: risks.filter((r) => r.rag === "red").length,
    amber: risks.filter((r) => r.rag === "amber").length,
    green: risks.filter((r) => r.rag === "green").length,
  };

  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-7xl flex-col gap-8 p-6 lg:gap-10 lg:p-10">
      <RouteHero
        eyebrow="Risk Register · Live"
        icon={AlertOctagon}
        numeral={String(counts.red).padStart(2, "0")}
        numeralTone="red"
        numeralSuffix={`/ ${String(risks.length).padStart(2, "0")}`}
        title="Where the assessment is tense"
        summary="Reds are the ones that can derail the cycle if left unattended. Track owners, mitigations, and gates here."
        meta={[
          { label: "Red", value: counts.red, tone: "red" },
          { label: "Amber", value: counts.amber, tone: "amber" },
          { label: "Green", value: counts.green, tone: "green" },
          { label: "Tracked", value: risks.length },
        ]}
        actions={
          <Link
            to="/risks/new"
            className="group inline-flex items-center justify-between gap-2 rounded-lg border border-[oklch(1_0_0_/_0.12)] bg-[oklch(1_0_0_/_0.04)] px-4 py-2.5 text-[13px] font-medium text-[oklch(0.93_0.008_240)] transition-all hover:border-[oklch(0.72_0.17_190_/_0.5)] hover:bg-[oklch(0.72_0.17_190_/_0.08)] print:hidden"
          >
            <span className="font-data text-[11px] tracking-[0.12em] uppercase">+ New risk</span>
            <ArrowUpRight className="size-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        }
      />

      <div>
        {risks.length === 0 ? (
          <p className="text-muted-foreground py-8 text-center text-xs italic">
            Risk register seeded from agent-os/docs/b2b/risks.md — apply migrations to populate.
          </p>
        ) : (
          <div className="glow-card-static p-4 lg:p-6">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground font-data text-left text-[10px] tracking-[0.18em] uppercase">
                <tr>
                  <th className="py-2 pr-4 font-medium">ID</th>
                  <th className="py-2 pr-4 font-medium">Risk</th>
                  <th className="py-2 pr-4 font-medium">RAG</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Owner</th>
                  <th className="py-2 pr-4 font-medium">Due</th>
                  <th className="py-2 font-medium" aria-label="Open" />
                </tr>
              </thead>
              <tbody>
                {risks.map((r) => (
                  <RiskRowItem
                    key={r.id}
                    risk={r}
                    ownerName={r.owner_id ? (ownersById.get(r.owner_id) ?? null) : null}
                    onTitleCommit={(next) => {
                      const trimmed = next.trim();
                      if (trimmed && trimmed !== r.title) {
                        updateRisk.mutate({ id: r.id, patch: { title: trimmed } });
                      }
                    }}
                    onRagChange={(rag) => updateRisk.mutate({ id: r.id, patch: { rag } })}
                    onStatusChange={(status) => updateRisk.mutate({ id: r.id, patch: { status } })}
                    onOpen={() => navigate({ search: { id: r.id } })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Sheet
        open={!!openId}
        onOpenChange={(open) => {
          if (!open) navigate({ search: {} });
        }}
      >
        <SheetContent side="right" className="w-full p-0 sm:max-w-2xl">
          <SheetTitle className="sr-only">Risk detail</SheetTitle>
          <SheetDescription className="sr-only">View and edit the full risk.</SheetDescription>
          {openId ? <RiskDrawerContent id={openId} /> : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function RiskRowItem({
  risk,
  ownerName,
  onTitleCommit,
  onRagChange,
  onStatusChange,
  onOpen,
}: {
  risk: RiskRow;
  ownerName: string | null;
  onTitleCommit: (next: string) => void;
  onRagChange: (rag: Rag) => void;
  onStatusChange: (status: Status) => void;
  onOpen: () => void;
}) {
  return (
    <tr className="border-border/40 hover:bg-muted/30 group border-t print:break-inside-avoid">
      <td className="text-muted-foreground py-2 pr-4 font-mono text-[10px]">{risk.code}</td>
      <td className="py-1.5 pr-4">
        <input
          key={risk.title}
          type="text"
          defaultValue={risk.title}
          aria-label="Edit risk title"
          className="focus:border-ring focus:bg-background hover:border-border/60 w-full truncate rounded-sm border border-transparent bg-transparent px-1.5 py-1 text-xs transition outline-none"
          onBlur={(e) => onTitleCommit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.blur();
            } else if (e.key === "Escape") {
              e.currentTarget.value = risk.title;
              e.currentTarget.blur();
            }
          }}
        />
      </td>
      <td className="py-1.5 pr-4">
        <Select value={risk.rag} onValueChange={(v) => onRagChange(v as Rag)}>
          <SelectTrigger
            className="hover:border-border/60 h-7 gap-1.5 border-transparent bg-transparent px-2 [&>svg:last-child]:opacity-50"
            aria-label="Change RAG"
          >
            <div className="inline-flex items-center gap-2">
              <StatusDot tone={risk.rag} />
              <span className="text-[10px] tracking-wide uppercase">{risk.rag}</span>
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="green">
              <span className="inline-flex items-center gap-2">
                <StatusDot tone="green" /> Green
              </span>
            </SelectItem>
            <SelectItem value="amber">
              <span className="inline-flex items-center gap-2">
                <StatusDot tone="amber" /> Amber
              </span>
            </SelectItem>
            <SelectItem value="red">
              <span className="inline-flex items-center gap-2">
                <StatusDot tone="red" /> Red
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      </td>
      <td className="py-1.5 pr-4">
        <Select value={risk.status} onValueChange={(v) => onStatusChange(v as Status)}>
          <SelectTrigger
            className="hover:border-border/60 h-7 gap-1.5 border-transparent bg-transparent px-2 [&>svg:last-child]:opacity-50"
            aria-label="Change status"
          >
            <div className="inline-flex items-center gap-2">
              <StatusDot tone={risk.status} />
              <span className="text-[11px]">{STATUS_LABEL[risk.status]}</span>
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="open">
              <span className="inline-flex items-center gap-2">
                <StatusDot tone="open" /> Open
              </span>
            </SelectItem>
            <SelectItem value="active">
              <span className="inline-flex items-center gap-2">
                <StatusDot tone="active" /> Active
              </span>
            </SelectItem>
            <SelectItem value="resolved">
              <span className="inline-flex items-center gap-2">
                <StatusDot tone="resolved" /> Resolved
              </span>
            </SelectItem>
            <SelectItem value="blocked">
              <span className="inline-flex items-center gap-2">
                <StatusDot tone="blocked" /> Blocked
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      </td>
      <td className="text-muted-foreground py-2 pr-4">{ownerName ?? "—"}</td>
      <td className="text-muted-foreground font-data py-2 pr-4 text-[11px] tabular-nums">
        {risk.due_date ? format(new Date(risk.due_date), "d MMM") : "—"}
      </td>
      <td className="py-2 print:hidden">
        <button
          type="button"
          onClick={onOpen}
          className="text-muted-foreground hover:bg-muted hover:text-foreground flex size-7 items-center justify-center rounded-sm opacity-0 transition group-hover:opacity-100 focus-visible:opacity-100"
          aria-label="Open risk detail"
          title="Open detail"
        >
          <Maximize2 className="size-3.5" />
        </button>
      </td>
    </tr>
  );
}
